import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { ENUM_HOME_FILTERS } from 'utils/enums';
import { ReactComponent as InfoIcon } from 'assets/icons/info-icon.svg';
import { ReactComponent as EmptyStateImage } from 'assets/images/empty-state.svg';
import PostCard from 'components/post-card';
import { useNavigate } from 'react-router-dom';
import { ROUTE_KEYS } from 'utils/constants';
import RecentActivityCard from 'components/recent-activity-card';
import { filterActivePosts, filterInactivePosts, handleApiError } from 'utils/functions';
import { apiService } from 'services/base.service';
import PageLoader from 'components/base/page-loader';
import useDashboardContext from 'contexts/dashboard.context';
import { PostType } from 'models/posts.model';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ActivityType } from 'models/activity.model';
import { ReactSortable } from 'react-sortablejs';
import { toast } from 'react-toastify';

const Home = () => {
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const { activeCokitchen } = useDashboardContext();
  const [activeFilter, setActiveFilter] = useState(ENUM_HOME_FILTERS.ALL);
  const [loading, setLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [posts, setPosts] = useState<PostType[]>([]);
  const [activePosts, setActivePosts] = useState<PostType[]>([]);
  const [activities, setActivities] = useState<ActivityType[]>([]);
  const [activityPage, setActivityPage] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  const [dragDisabled, setDragDisabled] = useState(true);
  const [arrangemendId, setArrangementId] = useState('');
  const navigateToCreatePost = () => {
    navigate(ROUTE_KEYS.CREATE_POST);
  };
  const PAGE_SIZE = 20;

  const fetchPosts = async () => {
    try {
      if (!activeCokitchen.id) return;
      setLoading(true);
      const res = await apiService.get('internal/cokitchen/home-page-header', {
        isInternal: true,
        params: {
          cokitchen_id: activeCokitchen.id
        }
      });
      const filteredPosts = res?.data?.data
        ?.find((c: { id: string }) => c.id === activeCokitchen.id)
        ?.cokitchen_home_page_posts?.find((p: { item_type: string }) => p.item_type === 'FOOD');
      setArrangementId(filteredPosts?.id);
      setPosts(filteredPosts?.posts);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [activeCokitchen.id]);
  const fetchActivity = async (resetActivities?: boolean) => {
    try {
      if (resetActivities && activityPage !== 1) return;
      setActivityLoading(true);
      const res = await apiService.get(`marketing/notification-logs`, {
        params: {
          cokitchen_id: activeCokitchen.id,
          page: activityPage,
          size: PAGE_SIZE
        }
      });
      setActivities(activityPage === 1 ? res.data.data : activities.concat(res.data.data));
      setTotalActivities(res.data.pagination.total);
    } catch (error) {
      handleApiError(error);
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, []);

  const postsToShow = useMemo(() => {
    if (activeFilter === ENUM_HOME_FILTERS.ALL) return posts;
    if (activeFilter === ENUM_HOME_FILTERS.ACTIVE) return filterActivePosts(posts);
    if (activeFilter === ENUM_HOME_FILTERS.INACTIVE) return filterInactivePosts(posts);
  }, [posts, activeFilter]);

  const rearrangePosts = async () => {
    try {
      const newArrangement = new Set();
      for (let i = 0; i < activePosts.length; i++) {
        newArrangement.add(activePosts[i]?.id);
      }
      for (let i = 0; i < posts.length; i++) {
        newArrangement.add(posts[i].id);
      }

      const res = await apiService.put('marketing/home-page-header', {
        data: {
          posts: Array.from(newArrangement),
          cokitchen_id: activeCokitchen.id,
          id: arrangemendId
        }
      });
      if (res.status === 200) {
        toast(<ToastMessage type='success' message={res.data?.message} />);
        setDragDisabled(true);
        fetchPosts();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  useEffect(() => {
    if (activeFilter === ENUM_HOME_FILTERS.ACTIVE) {
      setActivePosts(filterActivePosts(posts));
    }
  }, [posts, activeFilter]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (activeCokitchen.id) {
      setActivities([]);
      setActivityPage(1);
      fetchActivity(true);
    }
  }, [activeCokitchen.id]);

  const handleDelete = (id: string) => {
    setPosts(posts.filter((p) => p.id !== id));
  };

  return (
    <div className='px-10 py-7 flex items-stretch gap-6'>
      <section className='grow'>
        <div className='flex items-center justify-between flex-wrap mb-10 gap-4'>
          <div>
            <h2 className='text-gray-6 font-semibold text-2xl tracking-tight mb-3'>
              Manage homepage
            </h2>
            <p className='text-gray-8 text-sm'>
              Create & control posts that will be shown on the home page
            </p>
          </div>
          <Button
            onClick={navigateToCreatePost}
            color='primary'
            variant='block'
            small
            className='max-w-[139px] flex items-center gap-2 h-[41px] text-white'>
            <span className='text-2xl'>+</span>
            <span className='text-sm'>Create post</span>
          </Button>
        </div>

        <div className='flex justify-between border-b-[0.8px] border-gray-16 mb-6'>
          <div className='flex items-center gap-6 transition-all duration-500'>
            {Object.values(ENUM_HOME_FILTERS).map((f) => (
              <span
                onClick={() => setActiveFilter(f)}
                key={f}
                className={`pb-4 cursor-pointer ${
                  activeFilter === f
                    ? 'border-b-[3px] border-red-5 text-gray-6 font-medium'
                    : 'text-gray-5'
                }`}>
                {f}
              </span>
            ))}
          </div>
          {activeFilter === ENUM_HOME_FILTERS.ACTIVE && posts?.length > 1 && (
            <Fragment>
              {dragDisabled ? (
                <button
                  onClick={() => setDragDisabled(false)}
                  className='hover:bg-gray-light flex items-center gap-1 py-2 px-3 border border-gray-15 rounded-[20px] relative bottom-2'>
                  <InfoIcon />
                  <span className='text-sm text-gray-8'>Organize post</span>
                </button>
              ) : (
                <Button
                  onClick={rearrangePosts}
                  color='primary'
                  variant='block'
                  small
                  className='max-w-fit h-[30px] text-white relative bottom-2'>
                  <span className='text-sm'>Save changes</span>
                </Button>
              )}
            </Fragment>
          )}
        </div>

        {loading ? (
          <PageLoader />
        ) : postsToShow && postsToShow.length ? (
          <Fragment>
            {activeFilter === ENUM_HOME_FILTERS.ACTIVE && !dragDisabled ? (
              <ReactSortable
                animation={300}
                delay={1.5}
                delayOnTouchOnly
                disabled={dragDisabled}
                list={activePosts}
                setList={setActivePosts}
                className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'>
                {activePosts.map((post) => (
                  <PostCard key={post.id} post={post} handleDelete={() => handleDelete(post.id)} />
                ))}
              </ReactSortable>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'>
                {postsToShow.map((post) => (
                  <PostCard key={post.id} post={post} handleDelete={() => handleDelete(post.id)} />
                ))}
              </div>
            )}
          </Fragment>
        ) : (
          <div className='grid place-items-center'>
            <EmptyStateImage className='mt-10' />
            <p className='text-gray-17'>You have not created any post.</p>
          </div>
        )}
      </section>
      <section className='w-[350px] border-l border-gray-13'>
        <h4 className='text-sm text-gray-6 mb-6 font-medium ml-8'>Recent Activity</h4>
        {activityLoading ? (
          <PageLoader />
        ) : activities.length ? (
          <InfiniteScroll
            dataLength={activities.length}
            next={() => setActivityPage(activityPage + 1)}
            hasMore={activities.length < totalActivities}
            loader={<h4 className='text-center mt-2'>Loading...</h4>}
            endMessage={<p className='mt-2 text-center'>Yay! You have seen it all</p>}>
            {activities.map((activity) => (
              <div key={activity.id} className='ml-8'>
                <RecentActivityCard activity={activity} />
              </div>
            ))}
          </InfiniteScroll>
        ) : (
          <div className='grid place-items-center'>
            <EmptyStateImage className='mt-10' />
            <p className='text-gray-17'>No recent activity.</p>
          </div>
        )}
        <div></div>
      </section>
    </div>
  );
};

export default Home;
